import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell } from "recharts";
import { useFetch, fmtDate } from "../lib/api";
import { Card, PageHeader, StateBadge } from "../components/common";
import { SentTracker, OutreachResults } from "../components/OutreachTracking";

const COLORS = ["#2563EB", "#0F172A", "#10B981", "#F59E0B", "#64748B", "#06B6D4", "#EF4444"];

const Kpi = ({ label, value, tone = "text-slate-900", testid, to }) => {
  const inner = (
    <div data-testid={testid} className="h-full rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-colors duration-200 hover:border-blue-300">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-2 font-display text-3xl font-bold ${tone}`}>{value ?? "—"}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const days14 = (t) => Array.from({ length: 14 }, (_, i) => { const d = new Date(Date.now() - (13 - i) * 864e5).toISOString().slice(0, 10); return { name: d.slice(5), value: t?.[d] || 0 }; });
const toArr = (o) => Object.entries(o || {}).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

export default function Overview() {
  const { data: d } = useFetch("/dashboard");
  if (!d) return <div className="text-sm text-slate-500">Loading dashboard…</div>;
  const u = d.ai_usage;
  return (
    <div>
      <PageHeader title="Overview" subtitle="Live pipeline built only from discovered public evidence and your own CRM actions." />
      <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
        <Kpi testid="kpi-today" label="Discovered today" value={d.discovered_today} tone="text-blue-600" />
        <Kpi testid="kpi-month" label="Discovered this month" value={d.discovered_month} />
        <Kpi testid="kpi-new-clients" label="New potential clients (month)" value={d.new_companies_month} />
        <Kpi testid="kpi-strong" label="Strong skill match" value={d.match_strong} tone="text-emerald-600" />
        <Kpi testid="kpi-medium-match" label="Medium match" value={d.match_medium} tone="text-amber-600" />
        <Kpi testid="kpi-weak" label="Weak match" value={d.match_weak} tone="text-slate-500" />
        <Kpi testid="kpi-consulting" label="Consulting / contract vs permanent" value={`${d.consulting_projects} / ${d.permanent_jobs}`} />
        <Kpi testid="kpi-drafts" label="Drafts awaiting approval" value={d.drafts_pending} tone="text-amber-600" to="/admin/outreach" />
        <Kpi testid="kpi-sent" label="Outreach sent" value={d.outreach_sent} />
        <Kpi testid="kpi-interested" label="Interested prospects" value={d.interested} tone="text-emerald-600" />
        <Kpi testid="kpi-meetings-req" label="Meetings requested" value={d.meetings_requested} />
      </div>
      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div data-testid="kpi-estimated-value" className="rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Estimated potential value (not revenue)</div>
          <div className="mt-2 font-display text-3xl font-bold text-slate-900">{d.estimated_value_count ? `US$${Math.round(d.estimated_value_low).toLocaleString()} – ${Math.round(d.estimated_value_high).toLocaleString()}` : "—"}</div>
          <div className="mt-1 text-xs text-slate-500">From {d.estimated_value_count} opportunities with a published value, budget or rate. Estimates only.</div>
        </div>
        <div data-testid="kpi-confirmed-revenue" className="rounded-lg border border-emerald-300 bg-emerald-50/50 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Confirmed contract revenue</div>
          <div className="mt-2 font-display text-3xl font-bold text-emerald-700">US${Math.round(d.confirmed_revenue).toLocaleString()}</div>
          <div className="mt-1 text-xs text-slate-500">Sum of values you entered on won deals.</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
        <Kpi testid="kpi-companies" label="Companies" value={d.total_companies} to="/admin/companies" />
        <Kpi testid="kpi-active" label="Active opportunities" value={d.active_opportunities} to="/admin/opportunities" />
        <Kpi testid="kpi-high" label="High priority" value={d.high} tone="text-emerald-600" to="/admin/opportunities?priority=High" />
        <Kpi testid="kpi-medium" label="Medium priority" value={d.medium} tone="text-amber-600" to="/admin/opportunities?priority=Medium" />
        <Kpi testid="kpi-direct" label="Direct clients" value={d.direct} />
        <Kpi testid="kpi-partners" label="Consulting partners" value={d.partners} />
        <Kpi testid="kpi-followups" label="Follow-ups due" value={d.followups_due} tone="text-blue-600" to="/admin/followups" />
        <Kpi testid="kpi-contacted" label="Contacted" value={d.contacted} />
        <Kpi testid="kpi-replies" label="Replies" value={d.replies} />
        <Kpi testid="kpi-meetings" label="Meetings booked" value={d.meetings} />
        <Kpi testid="kpi-proposals" label="Proposals sent" value={d.proposals_sent} />
        <Kpi testid="kpi-won" label="Won" value={d.won} tone="text-emerald-600" />
        <Kpi testid="kpi-lost" label="Lost" value={d.lost} tone="text-red-600" />
        <Kpi testid="kpi-approvals" label="Awaiting approval" value={d.pending_approvals} tone="text-amber-600" to="/admin/outreach" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card title="Leads by country" testid="chart-country">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={toArr(d.by_country)} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Leads by technology" testid="chart-technology">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={toArr(d.by_technology)} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
              <Tooltip /><Bar dataKey="value" fill="#0F172A" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Engagement type" testid="chart-engagement">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart><Pie data={toArr(d.by_engagement)} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
              {toArr(d.by_engagement).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
          <div className="space-y-1">{toArr(d.by_engagement).map((x, i) => (
            <div key={x.name} className="flex items-center justify-between text-xs"><span className="flex items-center gap-2"><span className="h-2 w-2 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{x.name}</span><span className="font-mono">{x.value}</span></div>
          ))}</div>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card title="New leads discovered (14 days)" className="lg:col-span-2" testid="chart-trend">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={days14(d.trend)}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
              <Bar dataKey="value" fill="#2563EB" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="AI usage & estimated cost" testid="ai-usage-card">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-slate-500">Calls today</div><div className="font-mono text-xl">{u.today_calls}</div></div>
            <div><div className="text-xs text-slate-500">Cost today</div><div className="font-mono text-xl">${u.today_cost_usd}</div></div>
            <div><div className="text-xs text-slate-500">Calls this month</div><div className="font-mono text-xl">{u.month_calls}</div></div>
            <div><div className="text-xs text-slate-500">Est. cost this month</div><div className="font-mono text-xl" data-testid="ai-month-cost">${u.month_cost_usd}</div></div>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">Estimated from token counts × list price. Cache hits this month: {u.month_cached_hits}. Failed calls: {u.month_failed}.</p>
        </Card>
      </div>

      <Card title="Scheduled jobs" className="mt-6" testid="jobs-card">
        <div className="overflow-x-auto"><table className="dm-table w-full">
          <thead><tr><th>Job</th><th>Schedule switch</th><th>Last run</th><th>Last result</th><th>Last success</th><th>Next scheduled</th></tr></thead>
          <tbody>{Object.entries(d.jobs).map(([k, j]) => (
            <tr key={k} data-testid={`job-row-${k}`}>
              <td className="font-medium">{j.label}</td>
              <td>{j.enabled ? <StateBadge s="operational" /> : <StateBadge s="not_configured" />}<span className="ml-2 text-xs text-slate-500">{j.enabled ? "on" : "off"}</span></td>
              <td className="text-xs">{fmtDate(j.last_run?.started_at)}</td>
              <td>{j.last_run ? <StateBadge s={j.last_run.status} /> : <span className="text-xs text-slate-400">never run</span>}</td>
              <td className="text-xs">{fmtDate(j.last_success)}</td>
              <td className="text-xs">{j.enabled ? fmtDate(j.next_run) : "— (disabled)"}</td>
            </tr>))}</tbody>
        </table></div>
      </Card>
      <SentTracker />
      <OutreachResults compact />
    </div>
  );
}
